<!-- 코드뷰와 풀스크린 기능 관련 추가시
1. 코드뷰를 클릭할 때마다 이벤트를 발신하므로 @click:code-view="(event) => isSubmit = event"
  이런 식으로 사용하는 부모 컴포넌트에서 이벤트를 받아 코드뷰 상태에서는 submit하지 못하도록 수동설정 필요

2. 풀스크린 기능은 기본적으로 별도 설정 없이도 먹게 되어 있으나, 페이지 구조에 따라 섬머노트가 있는 컴포넌트가,
  다른 부모 컴포넌트에 갇혀있고, 해당 부모의 다른 형제 컴포넌트가 전체화면에서 보이는 경우가 있음.
  이런 경우에는 섬머노트의 부모 컴포넌트에 z-index를 줘야 함
-->

<template>
  <div>
    <div class="pa-3 summernote-wrapper dark:!text-white" fluid @contextmenu="onEditorContextMenu">
      <div :id="'summernote-' + uploaderId" />
    </div>

    <!-- 에디터 자체 이미지 업로드. 실제 board라는 db는 없으며, file이라는 필드도 없으나, 이는 게시판 파일업로드의 특성이며, 실제 file -->
    <CommonDYUploader v-model:modal-open="fileModal" v-model:upload-reset="fileReset" @upload-complete="fileUploaded"
      @cancel-complete="fileDeleted" :table-name="tableName" :subject-no="subjectNo ?? undefined" :field-name="fieldName"
      :max-files="100" upload-accept="all" :already-uploaded-count="0" :db-domain="dbDomain"
      :unique-id="uploadUniqueId"></CommonDYUploader>

    <!-- 2026-10-16 본문 이미지·영상 우클릭 교체 (imageReplace 를 켠 곳에서만). 관리자앱 디자인 폼의 우클릭 [이미지 수정] 과 같은 기능.
      업로드는 위 툴바 업로더와 같은 테이블·필드로 올리고 같은 upload:file 을 보내므로, 부모의 파일 연결 처리가 그대로 적용된다 -->
    <template v-if="imageReplace">
      <v-menu v-model="replaceMenu" :target="replaceMenuTarget" location="bottom start">
        <v-list density="compact">
          <v-list-item :title="replaceLabel" @click="openReplaceUploader" />
        </v-list>
      </v-menu>

      <CommonDYUploader v-model:modal-open="replaceModal" @upload-complete="replaceUploaded" :table-name="tableName"
        :subject-no="subjectNo ?? undefined" :field-name="fieldName" :max-files="1"
        :upload-accept="replaceTarget?.kind === 'video' ? 'video' : 'image'" :already-uploaded-count="0"
        :db-domain="dbDomain" :title="replaceLabel" :unique-id="uploadUniqueId"></CommonDYUploader>
    </template>
  </div>
</template>

<script setup lang='ts'>
import type { WatchStopHandle } from 'vue';
import { notify } from '@kyvg/vue3-notification';
import { useDYComponent } from '~/modules/nuxt3-module-doyac/composables/dy-component';
import ScriptLoader from '../libs/ScriptLoader';
import { uniqueId } from '../libs/utils';
import { useDYAuth } from '../composables/auth';
import { useDYAppConfig } from '../composables/config';
import { useSummerNote } from '~/modules/nuxt3-module-doyac/composables/dy-summernote.js'
import { useDYFileUploader, type SendUploadFiles } from '../composables/dy-file-uploader';
import { PASTED_IMAGE_MAX_MB, REJECTED_IMAGE_NOTICE, UPLOADING_IMAGE_ATTR, dataUrlToFile, isInlineDataImage, rejectImageElement, rejectInlineImagesInElement } from '../libs/editor-inline-image';

const dyComponent = useDYComponent()

const props = withDefaults(defineProps<{
  content?: string
  tableName?: string
  fieldName?: string
  /**
   * 섬머노트를 쓸 때 본체 db의 주키. 섬머노트 작동 특성상 이걸 넘기지 않아도 이미지 노출에는 
     문제가 없으나 본체의 fileTypedPk가 계속 바뀔 수 있으므로 삭제시 쓰레기 파일이 남을 가능성이 생겨버림
   */
  subjectNo?: number
  dbDomain?: string
  /**
   * 이미지 편집 레이어 문제가 생겨서 해당 기능을 끄기 위함
   */
  isDesignCustomize?: boolean
  defaultContentsHeight?: number
  /**
   * 본문의 이미지·영상·배경이미지를 우클릭해서 다른 파일로 교체하는 메뉴를 켠다(기본 꺼짐).
   * 관리자 전용으로 쓸 것 — 켜는 쪽에서 권한을 판정해 넘긴다
   */
  imageReplace?: boolean
  /**
   * 업로드 묶음 키(uniqueId). 부모 폼에 다른 업로더(관리자 디자인 폼의 우클릭 [이미지 수정] 등)가 있으면 같은 값을 넘겨 한 묶음에 올린다.
   * 안 넘기면 에디터마다 새로 뽑는다(기존 동작)
   */
  groupUniqueId?: number
}>(), {
  content: () => '',
  tableName: () => 'contents',
  fieldName: () => 'contentImage',
  isDesignCustomize: () => false,
  defaultContentsHeight: () => 400,
  imageReplace: () => false
})

const route = useRoute()
const { isAdmin } = useDYAuth()
const { siteName, fullDomain, basePath, appId } = useDYAppConfig()

const emit = defineEmits<{
  /**
   * 섬머노트를 보냄
   */
  (e: 'init', v: any): void
  (e: 'upload:file', v: SendUploadFiles): void
  (e: 'click:codeView', v: boolean): void
  (e: 'click:fullScreen', v: boolean): void
}>()

// ------------------ 인터페이스 ------------------

// ------------------ 데이터 ------------------

const uploaderId = ref<string>()
const summerNote = ref<any>()

const codeView = ref(false)
const fullScreen = ref(false)

// ------------------ 컴퓨티드 ------------------

// ------------------ 메서드 ------------------

const summerNoteSetup = () => {
  if (import.meta.client) {
    ScriptLoader.load([
      fullDomain + '/doyac/js/lib/jquery/jquery-3.4.1.slim.min.js',
      fullDomain + '/doyac/js/lib/summernote-0.8.18-dist/summernote-lite.min.js',
      fullDomain + '/doyac/js/lib/summernote/summernote-ko-KR.min.js'
    ], () => {
      const scriptEl = document.createElement('link')
      scriptEl.rel = 'stylesheet'
      scriptEl.href = (appId.includes('admin')) ? '/summernote/summernote-lite.min.css' : basePath + '/summernote/summernote-lite.min.css'

      document.getElementsByTagName('head')[0].appendChild(scriptEl)
      uploaderId.value = uniqueId()
      nextTick(() => {
        summerNote.value = useSummerNote(`summernote-${uploaderId.value}`, () => {
          fileModal.value = true
        })

        summerNote.value.set(isAdmin.value, route.params.boardName, siteName, props.isDesignCustomize, props.defaultContentsHeight,
          { onImageUpload: uploadPastedImages, onChange: handleInlineImages }) // 섬머노트 셋업

        document.getElementById('summernote-' + uploaderId.value)?.parentElement?.querySelector('.btn-fullscreen')?.addEventListener('click', () => {
          fullScreen.value = !fullScreen.value
          emit('click:fullScreen', fullScreen.value)
        })

        // document.getElementById('summernote-' + uploaderId.value)?.parentElement?.querySelector('.btn-codeview')?.addEventListener('click', () => {
          // codeView.value = !codeView.value
          // emit('click:codeView', codeView.value)
        // })

        emit('init', summerNote.value)
      })
    })
  }
}

//업로더 관련 데이터들
const fileModal = ref(false)
const fileReset = ref(false)
// 2026-10-17 이 에디터의 모든 업로드(툴바·우클릭 교체)가 같은 uniqueId 로 올라가게 고정 → 새 글에서도 파일 그룹이 하나만 생긴다.
//   업로더 기본값은 모달을 닫을 때마다(cancel-all) uniqueId 를 새로 뽑아 업로드마다 그룹이 따로 생기고, 부모에는 마지막 그룹만
//   연결됐다(contentImageFileData 덮어쓰기) → 앞 그룹 이미지는 글과 연결이 안 돼 글을 지워도 남거나, 주인없는 그룹 정리 때 지워짐
// 2026-10-20 부모가 groupUniqueId 를 주면 그 값을 쓴다 — 디자인 폼 새 등록 화면에서 툴바·우클릭 업로드가 서로 다른 묶음으로 갈라져
//   마지막 묶음만 섹션에 연결되던 문제
const uploadUniqueId = props.groupUniqueId ?? Math.random()

const fileUploaded = (param: SendUploadFiles) => {
  console.log('fileUploaded 호출됨, currentFileUrls:', param.currentFileUrls)
  param.currentFileUrls.forEach((file, index) => {
    console.log(`imageInsert 호출 ${index + 1}/${param.currentFileUrls.length}:`, file)
    summerNote.value.imageInsert(file)
  })
  emit('upload:file', param) //업로드 끝나고 해야하므로.. 타이밍 아래로 변경
}

const fileDeleted = () => {
  //
}

/**
 * 2026-10-20 붙여넣은(또는 이미지 창에서 고른) 이미지 파일을 툴바 업로드와 같은 테이블·필드·묶음으로 올리고 주소로 넣는다.
 *  섬머노트는 이 콜백이 없으면 이미지를 data:image… 로 본문에 그대로 박는다 — 1:1 문의 답변에 스크린샷을 붙여넣으며
 *  글 한 건이 수 MB 씩 불어났다(docs/EDITOR-PASTE-TW-CLEANUP.md)
 */
const { uploadEditorImage } = useDYFileUploader()
const editorUploadTarget = () => ({ tableName: props.tableName, fieldName: props.fieldName, uniqueId: uploadUniqueId, subjectNo: props.subjectNo, dbDomain: props.dbDomain })
const uploadPastedImages = async (files: ArrayLike<File>) => {
  for (const file of Array.from(files)) {
    if (!file.type.startsWith('image/')) continue
    if (file.size > PASTED_IMAGE_MAX_MB * 1000000) {
      notify({ type: 'warn', text: `${PASTED_IMAGE_MAX_MB}MB 를 넘는 이미지는 붙여넣을 수 없습니다. 이미지 업로드 버튼으로 올려 주세요.` })
      continue
    }
    try {
      fileUploaded(await uploadEditorImage(file, file.name || 'pasted-image.png', editorUploadTarget()))
    } catch (e) {
      // 서버 오류 문구(영문 "Server Error" 등)를 그대로 보이지 않고 다음 행동을 안내한다
      console.error('붙여넣은 이미지 업로드 실패', e)
      notify({ type: 'error', text: '이미지를 올리지 못했습니다. 이미지 업로드 버튼으로 다시 올려 주세요.' })
    }
  }
}

/**
 * 2026-10-20 본문에 박힌 이미지 데이터(base64)는 DB 에 넣지 않는다 — 웹페이지·문서에서 HTML 째 붙여넣은 이미지가 여기로 온다.
 *  - 업로드 메뉴가 있는 화면: 붙여넣는 순간 파일로 올리고 주소로 바꾼다(사용자 결정 — 툴바로 올린 것과 같은 결과)
 *  - 업로드를 막은 화면(업로드 메뉴 없는 게시판 — 섬머노트가 붙여넣은 이미지 파일도 base64 로 넣는다): ✕ 표시 + 첨부로 안내
 *  ✕ 표시는 등록할 때 빠진다(libs/editor-inline-image.ts)
 */
const handleInlineImages = (contents: string, $editable: any) => {
  if (!contents || contents.indexOf('data:') === -1) return
  const root: Element | undefined = $editable?.[0]
  if (!root) return

  if (!summerNote.value?.canUploadImage()) {
    if (rejectInlineImagesInElement(root)) notify({ type: 'warn', text: REJECTED_IMAGE_NOTICE })
    return
  }

  // onChange 는 입력마다 오므로 이미 올리는 중인 것은 건너뛴다
  const images = Array.from(root.querySelectorAll('img')).filter((img) => isInlineDataImage(img) && !img.hasAttribute(UPLOADING_IMAGE_ATTR))
  if (images.length) uploadInlineImages(images)
}

/**
 * 붙여넣은 data 이미지를 툴바 업로드와 같은 테이블·필드·묶음으로 올리고 그 자리의 주소만 바꾼다(크기·스타일 등 다른 속성은 그대로).
 *  올리는 동안은 흐리게 보이고(UPLOADING_IMAGE_ATTR) 저장 버튼은 isUploading() 으로 막힌다.
 *  못 올린 것(이미지 아님·크기 초과·업로드 실패)은 ✕ 로 바꾼다 — data 그대로 남겨 두면 저장 때 조용히 빠진다
 */
const uploadInlineImages = async (images: HTMLImageElement[]) => {
  images.forEach((img) => img.setAttribute(UPLOADING_IMAGE_ATTR, ''))

  let uploaded = 0
  let tooLarge = 0
  let failed = 0
  await Promise.all(images.map(async (img) => {
    const file = dataUrlToFile(img.getAttribute('src') ?? '')
    if (!file || file.size > PASTED_IMAGE_MAX_MB * 1000000) {
      if (file) tooLarge++
      else failed++
      if (img.isConnected) rejectImageElement(img)
      return
    }
    try {
      const param = await uploadEditorImage(file, file.name, editorUploadTarget())
      if (!img.isConnected) return // 올리는 사이 지운 이미지 — 올라간 파일은 툴바로 올렸다 지운 것과 같다(묶음에 남는다)
      ;['srcset', 'data-src', 'data-srcset'].forEach((attr) => img.removeAttribute(attr)) // 붙여넣은 원본의 다른 주소가 남지 않게
      img.src = param.currentFileUrls[0]
      img.removeAttribute(UPLOADING_IMAGE_ATTR)
      emit('upload:file', param) // 부모가 파일 묶음을 글에 연결한다(툴바 업로드와 같은 이벤트)
      uploaded++
    } catch (e) {
      console.error('붙여넣은 이미지 업로드 실패', e)
      failed++
      if (img.isConnected) rejectImageElement(img)
    }
  }))

  if (failed) notify({ type: 'error', text: `이미지 ${failed}개를 올리지 못했습니다(✕ 표시). 이미지 업로드 버튼으로 다시 올려 주세요.` })
  if (tooLarge) notify({ type: 'warn', text: `${PASTED_IMAGE_MAX_MB}MB 를 넘는 이미지 ${tooLarge}개는 올리지 않았습니다(✕ 표시). 이미지 업로드 버튼으로 올려 주세요.` })
  if (uploaded) notify({ type: 'success', text: `붙여넣은 이미지 ${uploaded}개를 업로드했습니다.` })
}

// ------------------ 우클릭 교체 (imageReplace) ------------------

type ReplaceKind = 'image' | 'video' | 'background'

const replaceMenu = ref(false)
const replaceMenuTarget = ref<[number, number]>([0, 0])
const replaceModal = ref(false)
const replaceTarget = shallowRef<{ el: HTMLElement, kind: ReplaceKind }>()

const replaceLabel = computed(() => {
  switch (replaceTarget.value?.kind) {
    case 'video': return '영상 교체'
    case 'background': return '배경 이미지 교체'
    default: return '이미지 교체'
  }
})

/**
 * 우클릭한 지점에서 교체할 대상을 찾는다. 본문(.note-editable) 안의 img·video, 아니면 인라인 배경이미지를 가진 조상.
 * 관리자앱은 요소마다 리스너를 붙여서 코드보기 전환·이미지 추가 때마다 다시 붙여야 했다 → 여기선 감싸는 div 하나에서 위임으로 받는다
 */
const findReplaceTarget = (start: EventTarget | null) => {
  if (!(start instanceof Element)) return undefined
  const editable = start.closest('.note-editable')
  if (!editable) return undefined

  const media = start.closest('img, video')
  if (media instanceof HTMLImageElement) return { el: media, kind: 'image' as const }
  if (media instanceof HTMLVideoElement) return { el: media, kind: 'video' as const }

  let el: Element | null = start
  while (el && el !== editable) {
    if (el instanceof HTMLElement && /url\(/i.test(el.style.backgroundImage)) return { el, kind: 'background' as const }
    el = el.parentElement
  }
  return undefined
}

/**
 * 섬머노트는 오른쪽 버튼 mousedown 에도 이미지 위에 선택 테두리(.note-control-selection)를 덮는다.
 * 크롬은 contextmenu 대상을 mouseup 뒤 화면 위치로 다시 잡으므로 대상이 이미지가 아니라 그 테두리(.note-editable 밖)가 된다
 * → 테두리 위 우클릭이면 그 지점 아래에 깔린 본문 요소를 찾아 쓴다. (관리자앱은 .note-handle 을 지워서 이 문제를 피했다)
 */
const resolveContextTarget = (e: MouseEvent) => {
  if (!(e.target instanceof Element) || !e.target.closest('.note-control-selection')) return e.target
  const editable = (e.currentTarget as HTMLElement | null)?.querySelector('.note-editable')
  if (!editable) return null
  return document.elementsFromPoint(e.clientX, e.clientY).find((el) => el !== editable && editable.contains(el)) ?? null
}

/**
 * 섬머노트 이미지 선택 테두리·팝오버를 숨긴다(다음 클릭 때 섬머노트가 다시 띄운다).
 * 우클릭에도 팝오버가 뜨는데, 그대로 두면 업로더 창 위로 겹쳐 그려져 드롭 영역을 가린다(운영 실측 2026-10-19)
 */
const hideImageHandle = () => {
  document.getElementById('summernote-' + uploaderId.value)?.parentElement
    ?.querySelectorAll<HTMLElement>('.note-control-selection, .note-image-popover')
    .forEach((elm) => elm.style.display = 'none')
}

const openReplaceUploader = () => {
  hideImageHandle()
  replaceModal.value = true
}

const onEditorContextMenu = (e: MouseEvent) => {
  if (!props.imageReplace) return

  const target = findReplaceTarget(resolveContextTarget(e))
  if (!target) return // 글자 위 우클릭은 브라우저 기본 메뉴(복사·맞춤법 등)를 그대로 둔다

  e.preventDefault()
  replaceTarget.value = target
  replaceMenuTarget.value = [e.clientX, e.clientY]
  nextTick(() => replaceMenu.value = true)
}

const replaceUploaded = (param: SendUploadFiles) => {
  const url = param.currentFileUrls[param.currentFileUrls.length - 1] // 툴바 업로드(fileUploaded)와 같은 원본 주소
  const target = replaceTarget.value
  replaceTarget.value = undefined
  if (!url || !target) return

  if (!target.el.isConnected) { // 업로드하는 사이 본문이 통째로 바뀐 경우(코드보기 편집 등)
    notify({ type: 'warn', text: '교체할 대상이 본문에서 사라져 교체하지 못했습니다. 다시 우클릭해 주세요.' })
    return
  }

  if (target.kind === 'background') {
    // 그라디언트 등 다른 레이어는 살리고 첫 번째 url() 만 바꾼다
    target.el.style.backgroundImage = target.el.style.backgroundImage.replace(/url\((['"]?).*?\1\)/i, `url("${url}")`)
  } else if (target.el instanceof HTMLImageElement) {
    // srcset·지연로딩 속성이 남아 있으면 src 를 바꿔도 옛 이미지가 보이거나 다시 돌아온다
    const img = target.el
    ;['srcset', 'sizes', 'data-src', 'data-srcset'].forEach((attr) => img.removeAttribute(attr))
    img.src = url
  } else if (target.el instanceof HTMLVideoElement) {
    target.el.querySelectorAll('source').forEach((source) => source.remove()) // 옛 주소가 html 에 남지 않도록
    target.el.src = url
    target.el.load()
  }

  hideImageHandle() // 선택 테두리가 옛 이미지 크기로 남는 것 정리

  emit('upload:file', param)
  notify({ type: 'success', text: `${target.kind === 'video' ? '영상을' : '이미지를'} 교체했습니다. 글을 등록(수정)해야 반영됩니다.` })
}

// ------------------ 훅 ------------------

// onMounted(() => {
//   summerNoteSetup()
// })

dyComponent.$onActivated(() => {
  summerNoteSetup()

  unwatch1 = watch(() => props.content, () => {
    if (summerNote.value) {
      summerNote.value.setContents(props.content)
    }
  })

  unwatch2 = watch(() => summerNote.value, () => {
    if (props.content) {
      summerNote.value.setContents(props.content)
    }
  })
})


let unwatch1: WatchStopHandle | undefined
let unwatch2: WatchStopHandle | undefined

dyComponent.$onDeactivated = () => {
  if (unwatch1) { unwatch1() }
  if (unwatch2) { unwatch2() }
  unwatch1 = undefined
  unwatch2 = undefined
}

</script>

<style lang='scss'>
.note-frame:where(.dark, .dark *) {
  color: #fff;
}

.note-frame.fullscreen .note-editable {
  background:white;
}

/* 2026-10-20 붙여넣은 이미지를 올리는 중(UPLOADING_IMAGE_ATTR) — 올리면 속성이 지워져 원래대로 보인다 */
.note-editable img[data-dy-uploading] {
  opacity: 0.4;
  outline: 2px dashed #1976d2;
}
</style>